// Recover a stale or HTML-poisoned lazy chunk after a deployment or DNS cutover.
// Chrome reports that as TypeError "Failed to fetch dynamically imported module".

export const CHUNK_RELOAD_KEY = 'v12-chunk-reload';

export function isDynamicImportError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  return (
    msg.includes('Failed to fetch dynamically imported module')
    || msg.includes('error loading dynamically imported module')
    || msg.includes('Importing a module script failed')
  );
}

export function moduleUrlFromError(error: unknown): string | null {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  const match = msg.match(/dynamically imported module:\s*(\S+)/i);
  if (!match) return null;
  const raw = match[1].replace(/[)'".,]+$/g, '');
  try {
    const origin = typeof window !== 'undefined' && window.location?.href
      ? window.location.href
      : 'http://localhost/';
    const url = new URL(raw, origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.href;
  } catch {
    return null;
  }
}

export async function bustCachedModule(error: unknown, fetchImpl: typeof fetch = fetch): Promise<void> {
  const url = moduleUrlFromError(error);
  if (!url || typeof window === 'undefined') return;
  const parsed = new URL(url);
  // An error message is not authority to request arbitrary URLs or APIs.
  if (parsed.origin !== new URL(window.location.href).origin || !parsed.pathname.startsWith('/assets/')) return;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 3000);
  try {
    await fetchImpl(url, { cache: 'reload', credentials: 'same-origin', signal: controller.signal });
  } catch {
    // Still reload; the cache bust is best-effort.
  } finally {
    clearTimeout(timeout);
  }
}

function defaultSessionStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

export async function autoRecoverDynamicImportOnce(
  error: unknown,
  reload: () => void,
  fetchImpl: typeof fetch = fetch,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = defaultSessionStorage(),
): Promise<boolean> {
  if (!isDynamicImportError(error)) return false;
  // A cross-navigation guard is mandatory. Private-mode/storage failures must
  // leave the manual Retry button available, never cause a reload loop.
  if (!storage) return false;
  try {
    if (storage.getItem(CHUNK_RELOAD_KEY)) return false;
    storage.setItem(CHUNK_RELOAD_KEY, '1');
    if (storage.getItem(CHUNK_RELOAD_KEY) !== '1') return false;
  } catch {
    return false;
  }
  await bustCachedModule(error, fetchImpl);
  reload();
  return true;
}
